<template>
  <div>
    <avue-crud
      ref="crud"
      v-model="form"
      :option="option"
      :table-loading="loading"
      :data="data"
      :page.sync="page"
      :search.sync="query"
      :defaults.sync="defaults"
      @row-save="rowSave"
      @row-update="rowUpdate"
      @row-del="rowDel"
      @search-change="searchChange"
      @search-reset="searchReset"
      @refresh-change="refreshChange"
      @current-change="currentChange"
      @size-change="sizeChange"
      @on-load="onLoad"
      @before-open="beforeOpen"
    >
      <template slot="menuLeft">
        <el-button
          type="primary"
          size="small"
          plain
          icon="el-icon-download"
          :loading="exportLoading"
          @click="handleExport"
        >导出</el-button>
      </template>
      <template slot="meterAttachmentsForm">
        <el-upload
          class="meter-attach-upload"
          :action="uploadAction"
          :headers="uploadHeaders"
          list-type="picture-card"
          :file-list="attachFileList"
          :limit="8"
          :multiple="true"
          accept="image/*"
          :before-upload="beforeAttachUpload"
          :on-success="onAttachSuccess"
          :on-remove="onAttachRemove"
          :on-exceed="onAttachExceed"
        >
          <i class="el-icon-plus" />
        </el-upload>
        <div class="upload-tip">上传至 OSS（put-file-attach），支持多张现场抄表照片；保存时写入库表字段 meter_attachments（逗号分隔 URL）。</div>
      </template>
    </avue-crud>
  </div>
</template>

<script>
import crudMixins from '@/mixins/crud-mixins'
import { FEE_TYPE_OPTIONS, CATEGORY_BY_FEE, feeTypeLabel } from '../constants'
import { fetchPage, insertRecord, updateRecord, deleteRecord, exportExcel } from './api'
import { getToken } from '@/util/auth'
import website from '@/config/website'
import { downloadXls } from '@/util/util'

export default {
  name: 'MeterRecordCrud',
  mixins: [crudMixins],
  props: {
    /** 本页允许的费目 value，与 constants 中 FEE_TYPE_OPTIONS 一致 */
    allowedFeeTypes: {
      type: Array,
      required: true
    },
    pageTitle: {
      type: String,
      required: true
    },
    directUsageEntry: {
      type: Boolean,
      default: false
    },
    usageLabel: {
      type: String,
      default: '本期用量'
    }
  },
  data() {
    const types = this.allowedFeeTypes || []
    const queryInit = types.length ? { feeType: types[0] } : {}
    return {
      defaultParams: { ...queryInit },
      query: { ...queryInit },
      option: {},
      attachFileList: [],
      exportLoading: false,
      apiConfig: {
        getList: (params) => fetchPage(params)
      }
    }
  },
  computed: {
    feeDic() {
      return FEE_TYPE_OPTIONS.filter((o) => this.allowedFeeTypes.includes(o.value)).map((o) => ({
        label: o.label,
        value: o.value
      }))
    },
    uploadAction() {
      return '/api/blade-resource/oss/endpoint/put-file-attach'
    },
    uploadHeaders() {
      const t = getToken()
      if (!t) return {}
      const bearer = 'Bearer ' + t
      return {
        [website.tokenHeader]: bearer,
        Authorization: bearer
      }
    }
  },
  watch: {
    form() {
      this.$nextTick(() => this.syncBillPeriodRangeFromFields())
    },
    'form.billPeriodStart'() {
      this.syncBillPeriodRangeFromFields()
    },
    'form.billPeriodEnd'() {
      this.syncBillPeriodRangeFromFields()
    }
  },
  created() {
    this.option = this.buildOption()
    this.queryAutoArrange()
  },
  methods: {
    feeTypeLabel,
    /** 混入 created 早于本组件赋值 option 时调用，需防空 */
    initSearchValue() {
      if (!this.option || !Array.isArray(this.option.column)) return
      this.option.column.forEach((item) => {
        if (item.searchValue) this.$set(this.query, item.prop, item.searchValue)
      })
    },
    buildSearchCategoryDic() {
      const seen = new Set()
      const out = []
      this.allowedFeeTypes.forEach((ft) => {
        (CATEGORY_BY_FEE[ft] || []).forEach((c) => {
          if (!seen.has(c)) {
            seen.add(c)
            out.push({ label: c, value: c })
          }
        })
      })
      return out
    },
    validateBillPeriodRange(rule, value, callback) {
      if (!value || !Array.isArray(value) || value.length !== 2 || !value[0] || !value[1]) {
        callback(new Error('请选择'))
        return
      }
      if (value[1] < value[0]) {
        callback(new Error('结束日期不能早于开始日期'))
        return
      }
      callback()
    },
    syncBillPeriodRangeFromFields() {
      if (!this.directUsageEntry || !this.form) return
      const start = this.form.billPeriodStart
      const end = this.form.billPeriodEnd
      const next = start && end ? [start, end] : []
      const current = this.form.billPeriodRange
      const same = Array.isArray(current) &&
        current.length === next.length &&
        current[0] === next[0] &&
        current[1] === next[1]
      if (!same) {
        this.$set(this.form, 'billPeriodRange', next)
      }
    },
    buildOption() {
      const that = this
      const searchCatDic = this.buildSearchCategoryDic()
      const usageRules = this.directUsageEntry ? [{ required: true, message: '必填', trigger: 'change' }] : []
      const readingDateRules = this.directUsageEntry ? [] : [{ required: true, message: '必选', trigger: 'change' }]
      return {
        ...this.$store.state.publicOption,
        menu: true,
        addBtnText: '新增',
        addBtn: true,
        editBtn: true,
        delBtn: true,
        viewBtn: true,
        dialogWidth: '720px',
        column: [
          {
            label: 'id',
            prop: 'id',
            hide: true,
            addDisplay: false,
            editDisplay: false,
            viewDisplay: false
          },
          {
            label: '账单月',
            prop: 'billingMonth',
            width: 88,
            search: true,
            type: 'month',
            format: 'yyyyMM',
            valueFormat: 'yyyyMM',
            addDisplay: !this.directUsageEntry,
            editDisplay: !this.directUsageEntry,
            overHidden: true
          },
          {
            label: '费目',
            prop: 'feeType',
            type: 'select',
            dicData: this.feeDic,
            clearable: false,
            width: 88,
            search: true,
            formatter: (row) => feeTypeLabel(row.feeType),
            rules: [{ required: true, message: '必选', trigger: 'change' }],
            change({ value }) {
              const prev = that._lastFeeTypeForCategory
              that._lastFeeTypeForCategory = value
              if (prev !== undefined && prev !== null && prev !== '' && value !== prev) {
                that.form.category = ''
              }
            }
          },
          {
            label: '类目',
            prop: 'category',
            type: 'select',
            filterable: true,
            allowCreate: true,
            minWidth: 120,
            overHidden: true,
            search: true,
            searchFilterable: true,
            dicData: searchCatDic,
            rules: [{ required: true, message: '必选', trigger: 'change' }]
          },
          {
            label: '上期表底',
            prop: 'lastReadingValue',
            width: 110,
            align: 'right',
            addDisplay: false,
            editDisplay: false,
            hide: this.directUsageEntry,
            viewDisplay: !this.directUsageEntry
          },
          {
            label: '上期抄表日',
            prop: 'lastReadingDate',
            width: 110,
            addDisplay: false,
            editDisplay: false,
            hide: this.directUsageEntry,
            viewDisplay: !this.directUsageEntry
          },
          {
            label: this.directUsageEntry ? this.usageLabel : '本期表底',
            prop: 'currentReadingValue',
            type: 'number',
            precision: 4,
            step: 0.01,
            min: 0,
            width: 110,
            align: 'right',
            controls: false,
            hide: this.directUsageEntry,
            addDisplay: !this.directUsageEntry,
            editDisplay: !this.directUsageEntry,
            rules: this.directUsageEntry ? [] : [{ required: true, message: '必填', trigger: 'change' }]
          },
          {
            label: this.usageLabel,
            prop: 'usageAmount',
            type: 'number',
            precision: this.directUsageEntry ? 2 : 4,
            step: 0.01,
            min: 0,
            width: 100,
            align: 'right',
            controls: false,
            addDisplay: this.directUsageEntry,
            editDisplay: this.directUsageEntry,
            rules: usageRules
          },
          {
            label: '本期费用',
            prop: 'currentFeeAmount',
            type: 'number',
            precision: 2,
            step: 0.01,
            min: 0,
            width: 110,
            align: 'right',
            controls: false,
            rules: []
          },
          {
            label: '账单周期',
            prop: 'billPeriodRange',
            type: 'daterange',
            format: 'yyyy-MM-dd',
            valueFormat: 'yyyy-MM-dd',
            startPlaceholder: '开始日期',
            endPlaceholder: '结束日期',
            span: 12,
            hide: true,
            showColumn: false,
            addDisplay: this.directUsageEntry,
            editDisplay: this.directUsageEntry,
            viewDisplay: false,
            rules: this.directUsageEntry ? [{ required: true, validator: this.validateBillPeriodRange, trigger: 'change' }] : []
          },
          {
            label: '账单周期开始',
            prop: 'billPeriodStart',
            width: 120,
            type: 'date',
            format: 'yyyy-MM-dd',
            valueFormat: 'yyyy-MM-dd',
            addDisplay: false,
            editDisplay: false,
            hide: !this.directUsageEntry,
            rules: []
          },
          {
            label: '账单周期结束',
            prop: 'billPeriodEnd',
            width: 120,
            type: 'date',
            format: 'yyyy-MM-dd',
            valueFormat: 'yyyy-MM-dd',
            addDisplay: false,
            editDisplay: false,
            hide: !this.directUsageEntry,
            rules: []
          },
          {
            label: '账单打印日期',
            prop: 'billPrintDate',
            width: 120,
            type: 'date',
            format: 'yyyy-MM-dd',
            valueFormat: 'yyyy-MM-dd',
            addDisplay: this.directUsageEntry,
            editDisplay: this.directUsageEntry,
            hide: !this.directUsageEntry
          },
          {
            label: '本次抄表日',
            prop: 'readingDate',
            width: 110,
            type: 'date',
            format: 'yyyy-MM-dd',
            valueFormat: 'yyyy-MM-dd',
            addDisplay: !this.directUsageEntry,
            editDisplay: !this.directUsageEntry,
            hide: this.directUsageEntry,
            rules: readingDateRules
          },
          {
            label: '抄表人',
            prop: 'readerName',
            width: 90,
            maxlength: 100,
            addDisplay: !this.directUsageEntry,
            editDisplay: !this.directUsageEntry,
            hide: this.directUsageEntry,
            overHidden: true
          },
          {
            label: '备注',
            prop: 'remark',
            type: 'textarea',
            minRows: 2,
            maxlength: 500,
            showWordLimit: true,
            span: 24,
            minWidth: 100,
            addDisplay: !this.directUsageEntry,
            editDisplay: !this.directUsageEntry,
            hide: this.directUsageEntry,
            overHidden: true
          },
          {
            label: '抄表附件',
            prop: 'meterAttachments',
            formslot: true,
            span: 24,
            hide: true,
            addDisplay: !this.directUsageEntry,
            editDisplay: !this.directUsageEntry,
            viewDisplay: true
          },
          {
            label: '抄表日期',
            prop: 'readingDateRange',
            type: 'daterange',
            search: !this.directUsageEntry,
            searchRange: true,
            hide: true,
            showColumn: false,
            format: 'yyyy-MM-dd',
            valueFormat: 'yyyy-MM-dd',
            paramsKey: ['readingDateStart', 'readingDateEnd'],
            addDisplay: false,
            editDisplay: false,
            viewDisplay: false
          }
        ]
      }
    },
    searchCategoryOptions(feeType) {
      if (feeType != null && feeType !== '') {
        return (CATEGORY_BY_FEE[feeType] || []).map((c) => ({ label: c, value: c }))
      }
      return this.buildSearchCategoryDic()
    },
    searchChange(params, done) {
      const opts = this.searchCategoryOptions(params.feeType)
      if (params.category && !opts.some((o) => o.value === params.category)) {
        params.category = ''
      }
      this.query = params
      this.page.currentPage = 1
      this.selectionClear()
      this.onLoad(this.page, params)
      done()
    },
    searchReset() {
      const types = this.allowedFeeTypes || []
      this.query = types.length ? { feeType: types[0] } : {}
      this.page.currentPage = 1
      this.selectionClear()
      this.initSearchValue()
      this.onLoad(this.page, this.query)
    },
    beforeOpen(done, type) {
      if (type === 'add') {
        this.attachFileList = []
        this._lastFeeTypeForCategory = undefined
        if (this.directUsageEntry) {
          const defaultFeeType = this.query.feeType || (this.allowedFeeTypes && this.allowedFeeTypes[0])
          this.$set(this.form, 'feeType', defaultFeeType)
          this.$set(this.form, 'usageAmount', undefined)
          this.$set(this.form, 'currentReadingValue', undefined)
          this.$set(this.form, 'readingDate', '')
          this.$set(this.form, 'billingMonth', '')
          this.$set(this.form, 'billPeriodRange', [])
          this.$set(this.form, 'billPeriodStart', '')
          this.$set(this.form, 'billPeriodEnd', '')
        }
      } else {
        this.hydrateAttachFileList(this.form.meterAttachments || '')
        const ft = this.form.feeType
        if (ft != null && ft !== '') {
          const n = Number(ft)
          if (!Number.isNaN(n)) this.$set(this.form, 'feeType', n)
        }
        this._lastFeeTypeForCategory = this.form.feeType
        if (this.directUsageEntry) {
          this.$nextTick(() => this.syncBillPeriodRangeFromFields())
        }
      }
      done()
    },
    hydrateAttachFileList(csv) {
      if (!csv || !String(csv).trim()) {
        this.attachFileList = []
        return
      }
      this.attachFileList = String(csv)
        .split(',')
        .map((url, i) => {
          const u = url.trim()
          if (!u) return null
          let name = u.split('/').pop() || '抄表照片' + (i + 1)
          try {
            name = decodeURIComponent(name)
          } catch (e) {
            /* ignore */
          }
          return { name, url: u, uid: 'meter-att-' + i + '-' + u.slice(-12) }
        })
        .filter(Boolean)
    },
    syncMeterAttachmentsFromFileList() {
      const urls = (this.attachFileList || [])
        .map((f) => {
          if (f.url) return f.url
          const r = f.response
          return r && r.data && r.data.link
        })
        .filter(Boolean)
      this.form.meterAttachments = urls.join(',')
    },
    beforeAttachUpload(file) {
      const max = 10 * 1024 * 1024
      if (file.size > max) {
        this.$message.error('单张图片不超过 10MB')
        return false
      }
      return true
    },
    onAttachSuccess(res, file, fileList) {
      const url = res && res.data && res.data.link
      if (url) this.$set(file, 'url', url)
      this.attachFileList = fileList
    },
    onAttachRemove(file, fileList) {
      this.attachFileList = fileList
    },
    onAttachExceed() {
      this.$message.warning('最多上传 8 个附件')
    },
    validateFeeType(payload) {
      const v = Number(payload.feeType)
      if (Number.isNaN(v) || !this.allowedFeeTypes.includes(v)) {
        this.$message.error('费目不在本页允许范围内')
        return false
      }
      return true
    },
    normalizeCurrentFeePayload(payload, isEdit) {
      if (payload.currentFeeAmount === '') {
        payload.currentFeeAmount = null
      }
      if (payload.currentFeeAmount === null || payload.currentFeeAmount === undefined) {
        if (isEdit) payload.clearCurrentFeeAmount = true
      } else if (isEdit) {
        payload.clearCurrentFeeAmount = false
      }
      return payload
    },
    normalizeUsagePayload(payload) {
      if (!this.directUsageEntry) return payload
      const billPeriodRange = payload.billPeriodRange
      if (Array.isArray(billPeriodRange) && billPeriodRange.length === 2) {
        payload.billPeriodStart = billPeriodRange[0]
        payload.billPeriodEnd = billPeriodRange[1]
      }
      delete payload.billPeriodRange
      const value = payload.usageAmount !== undefined && payload.usageAmount !== null && payload.usageAmount !== ''
        ? payload.usageAmount
        : payload.currentReadingValue
      if (value !== undefined && value !== null && value !== '') {
        payload.usageAmount = value
        payload.currentReadingValue = value
      }
      if (payload.billPeriodEnd && !payload.readingDate) {
        payload.readingDate = payload.billPeriodEnd
      }
      if (payload.billPeriodStart && !payload.billingMonth) {
        payload.billingMonth = String(payload.billPeriodStart).slice(0, 7).replace('-', '')
      }
      return payload
    },
    rowSave(row, done, loading) {
      this.syncMeterAttachmentsFromFileList()
      const payload = this.normalizeCurrentFeePayload(this.normalizeUsagePayload({ ...this.form, ...row }), false)
      if (!this.validateFeeType(payload)) {
        loading()
        return
      }
      insertRecord(payload)
        .then(() => {
          this.$message.success('已新增')
          this.selectionClear()
          this.onLoad(this.page, this.query)
          done()
        })
        .catch(() => loading())
    },
    rowUpdate(row, index, done, loading) {
      this.syncMeterAttachmentsFromFileList()
      const payload = this.normalizeCurrentFeePayload(this.normalizeUsagePayload({ ...this.form, ...row }), true)
      if (!this.validateFeeType(payload)) {
        loading()
        return
      }
      updateRecord(payload)
        .then(() => {
          this.$message.success('已更新')
          this.selectionClear()
          this.onLoad(this.page, this.query)
          done()
        })
        .catch(() => loading())
    },
    rowDel(row) {
      this.$confirm('确认删除该抄表记录？', '提示', { type: 'warning' })
        .then(() => deleteRecord(row.id))
        .then(() => {
          this.$message.success('已删除')
          this.selectionClear()
          this.onLoad(this.page, this.query)
        })
        .catch((e) => {
          if (e === 'cancel' || e === 'close') return
          this.$message.error((e && e.message) || '删除失败')
        })
    },
    async handleExport() {
      const total = this.page.total || 0
      if (total < 1) {
        this.$message.warning('无数据可导出')
        return
      }
      const maxRows = 10000
      if (total > maxRows) {
        this.$message.warning(`当前最多导出 ${maxRows} 条，请缩小筛选范围后重试`)
        return
      }
      this.exportLoading = true
      try {
        const params = this.formatParams(Object.assign({}, this.defaultParams, this.query))
        const res = await exportExcel(params)
        downloadXls(res.data, `${this.pageTitle}_${Date.now()}.xlsx`)
        this.$message.success('导出成功')
      } catch (e) {
        console.warn(e)
        this.$message.error((e && e.message) || '导出失败')
      } finally {
        this.exportLoading = false
      }
    }
  }
}
</script>

<style scoped>
.table-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  line-height: 32px;
}
.meter-attach-upload ::v-deep .el-upload--picture-card {
  line-height: 148px;
}
.upload-tip {
  margin-top: 8px;
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
}
</style>
